import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';
import { isPushSupported, subscribeUserToPush, unsubscribeFromPush } from '../lib/pushService';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function PushToggleCard() {
  const { user } = useAuth();
  const { lang } = useT();
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const supported = isPushSupported();

  useEffect(() => {
    if (!supported) return;
    setPermission(Notification.permission);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setSubscribed(!!sub))
        .catch(() => {});
    }
  }, [supported]);

  if (!supported) {
    return (
      <div className="card p-4 border-2 border-amber-200 bg-amber-50">
        <div className="flex items-start gap-3">
          <BellOff size={20} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-relaxed">
            {lang === 'fr'
              ? "Les notifications push ne sont pas supportées sur ce navigateur. Sur iPhone : ajoutez l'app à l'écran d'accueil (PWA) puis ouvrez-la depuis l'icône."
              : 'Push notifications are not supported in this browser. On iPhone: install the app to your Home Screen (PWA) then open it from there.'}
          </div>
        </div>
      </div>
    );
  }

  const enable = async () => {
    if (!user?.id) return;
    setBusy(true);
    try {
      const res = await subscribeUserToPush(user.id);
      setPermission(Notification.permission);
      if (res?.status === 'subscribed') {
        setSubscribed(true);
        toast.success(
          lang === 'fr'
            ? '✓ Notifications activées'
            : '✓ Notifications enabled'
        );
      } else if (res?.status === 'denied') {
        toast.error(
          lang === 'fr'
            ? "Permission refusée. Activez-les dans les réglages du navigateur."
            : 'Permission denied. Enable it in your browser settings.'
        );
      } else {
        toast.error(lang === 'fr' ? "Activation impossible" : 'Could not enable');
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast.success(lang === 'fr' ? '✓ Notifications désactivées' : '✓ Notifications disabled');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4 border-2 border-[#E5DCC9]">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          subscribed ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {subscribed ? <BellRing size={20} /> : <Bell size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-base">
            {lang === 'fr' ? 'Alertes de signature' : 'Signature alerts'}
          </div>
          <div className="text-xs text-gray-600 mt-1 leading-relaxed">
            {lang === 'fr'
              ? 'Recevez une notification sur votre téléphone dès qu\u2019un client signe une soumission, même si l\u2019app est fermée.'
              : 'Get a notification on your phone the moment a client signs a quote, even if the app is closed.'}
          </div>
          {permission === 'denied' && (
            <div className="text-[11px] text-red-700 mt-2 font-bold">
              {lang === 'fr'
                ? 'Permission bloquée. Réactivez les notifications dans les réglages du navigateur.'
                : 'Blocked. Re-enable notifications in your browser settings.'}
            </div>
          )}
          <div className="mt-3">
            {subscribed ? (
              <button
                data-testid="push-disable"
                onClick={disable}
                disabled={busy}
                className="btn-outline gap-2 !min-h-[40px] !text-sm !text-red-700 !border-red-300 hover:!bg-red-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <BellOff size={14} />}
                {lang === 'fr' ? 'Désactiver' : 'Disable'}
              </button>
            ) : (
              <button
                data-testid="push-enable"
                onClick={enable}
                disabled={busy || permission === 'denied'}
                className="btn-primary gap-2 !min-h-[40px] !text-sm"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
                {lang === 'fr' ? 'Activer les notifications' : 'Enable notifications'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
